import React, { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import UserInfo from "./stats-box";
import EditableUserInfo from "./stats-box-edit";
import { url } from "../../api/index.js";
import styled from "styled-components";
import FullStats from "./big-stats-box";
import AotdAuthorsLeaderboard from "./AOTDLeaderboard";
import AdminNavLinks from "../admin/AdminNavLinks";
import UserStatsTable from "./allUserStats";
import { apiClient } from "../../api/client";
import RespectAdminPanel from "../home_page/RespectAdminPanel";
import ManualIdentityLinkPanel from "./ManualIdentityLinkPanel";
import { HomeBannerSettings } from "../../features/homeBanner";
import type { RootState } from "../../store/store";


const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #c9febe;
  align-items: center;
  padding: 15px;
  height: auto;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 640px) {
    padding: 10px 8px;
  }
`;

interface Stats {
  totalUsers: number;
  totalFriendsFamilyUsers: number;
  totalAOTD: number;
  totalMessages: number;
  totalTokensUsed: number;
  usersWithTheMostTokens: Array<any>;
  aotdAuthors: Array<any>;
}


interface User {
  id: number;
  username: string;
  userEmoji: string;
  email: string;
  role: string;
  dateSigned: string;
  birthDate?: string | null;
  timesSigned: number;
  chatTokensUsed: number;
  permissions: number;
}

interface UserListState {
  users: User[];
  editingUserIds: Set<number>;
  editedUsernames: string[];
  fetchError: string | null;
}

const mapState = (state: RootState) => ({
  authToken: state.auth.token,
  bootstrapStatus: state.auth.bootstrapStatus,
});

const connector = connect(mapState);
type Props = ConnectedProps<typeof connector>;

class UserList extends Component<Props, UserListState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      users: [],
      editingUserIds: new Set(),
      editedUsernames: [],
      fetchError: null,
    };
  }

  componentDidMount() {
    this.tryFetchUsers();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.bootstrapStatus !== this.props.bootstrapStatus ||
      prevProps.authToken !== this.props.authToken
    ) {
      this.tryFetchUsers();
    }
  }

  tryFetchUsers() {
    if (this.props.bootstrapStatus !== "ready") {
      return;
    }
    if (!this.props.authToken) {
      this.setState({
        fetchError: "Sign in to load the user list (admin only).",
        users: [],
      });
      return;
    }
    void this.fetchUsers();
  }

  async fetchUsers() {
    if (this.props.bootstrapStatus !== "ready" || !this.props.authToken) {
      return;
    }
    try {
      const response = await apiClient.get(`${url}/api/stats/all-users`);
      if (response.status !== 200) {
        this.setState({
          fetchError: `Unexpected response (${response.status}).`,
          users: [],
        });
        return;
      }
      const users = response.data;
      this.setState({ users, fetchError: null });
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      const status = err?.response?.status;
      let message = "Could not load users.";
      if (status === 401) {
        message =
          "Not authorized (401). Try refreshing the page after sign-in, or check that the access token is being sent.";
      } else if (status === 403) {
        message = "Forbidden — this list is only for admins.";
      }
      console.error("UserList.fetchUsers", error);
      this.setState({ users: [], fetchError: message });
    }
  }

  // user stats form /api/stats/users
  // return a dict of users and their stats
  async fetchUserStats() {
    try {
      const response = await apiClient.get(`${url}/api/stats/users`);
      if (response.status !== 200) {
        return;
      }
      const userStats = response.data;
      // the format is:
      // return {
      //   username: user.username,
      //   quoteCount: userQuoteCount ? userQuoteCount.quotesCount : 0,
      //   longestStreak: {
      //     "count": userStreak ? userStreak.longestStreak : 0,
      //     streakStartDate: userStreak ? userStreak.startDate : null,
      //     streakEndDate: userStreak ? userStreak.endDate : null
      //   }
      //   };
      //   });
      return userStats;
    } catch (error) {
      console.error("Error fetching user stats:", error);
    }
  }

  handleFilterChange = async (filterData: any) => {
    const { searchText } = filterData;
    const queryParams = new URLSearchParams();
    if (searchText) {
      queryParams.append("username", searchText);
    }
    // Add other filter parameters to queryParams if needed

    const response = await fetch(
      `https://api.example.com/users?${queryParams.toString()}`
    );
    const users = await response.json();
    this.setState({ users });
  };


  handleEditButtonClick = (username: string) => {
    this.setState((prevState) => ({
      editedUsernames: [username]
    }));
  };

  handleCancelEdit = (username: string) => {
    const editedUsernames = this.state.editedUsernames.filter(
      (element) => element !== username
    );
    this.setState({ editedUsernames });
  };


  handleSubmit = async (username: string, updatedUserData: any) => {
    try {
      await apiClient.put(`${url}/api/stats/user/${username}`, updatedUserData);
      await this.fetchUsers();
      const editedUsernames = this.state.editedUsernames.filter(
        (element) => element !== username
      );
      this.setState({ editedUsernames });
    } catch (error) {
      console.error("UserList.handleSubmit", error);
      this.setState({ fetchError: "Could not save user changes." });
    }
  };

  render() {
    return (
      <PageContainer>
        <HomeBannerSettings />
        {/* respect admin rides along in the "cards & economy" group instead of
            floating on its own between the button wall and the stats */}
        <AdminNavLinks extras={{ cards: <RespectAdminPanel /> }} />
        <ManualIdentityLinkPanel />
        {this.state.fetchError && (
          <p
            style={{
              color: "#7a1f1f",
              background: "#ffecec",
              border: "1px solid #e8a0a0",
              borderRadius: 8,
              padding: "12px 16px",
              maxWidth: 560,
              marginTop: 12,
            }}
          >
            {this.state.fetchError}
          </p>
        )}
        <FullStats />
        <UserStatsTable />
        <AotdAuthorsLeaderboard />
        {/* <UserFilter onFilterChange={this.handleFilterChange} /> */}
        {this.state.users.map((user) => {
          if (this.state.editedUsernames.includes(user.username)) {
            return (
              <EditableUserInfo
                key={user.id}
                username={user.username}
                userEmoji={user.userEmoji}
                email={user.email}
                role={user.role}
                dateSigned={user.dateSigned}
                birthDate={user.birthDate}
                timesSigned={user.timesSigned}
                chatTokensUsed={user.chatTokensUsed}
                permissions={user.permissions}
                onSubmit={(updatedUserData) =>
                  this.handleSubmit(user.username, updatedUserData)
                }
                onCancel={() => this.handleCancelEdit(user.username)}
              />
            );
          } else {
            return (
              <UserInfo
                key={user.id}
                username={user.username}
                userEmoji={user.userEmoji}
                email={user.email}
                role={user.role}
                dateSigned={user.dateSigned}
                birthDate={user.birthDate}
                timesSigned={user.timesSigned}
                chatTokensUsed={user.chatTokensUsed}
                permissions={user.permissions}
                onEditButtonClick={() =>
                  this.handleEditButtonClick(user.username)
                }
              />
            );
          }
        })}
      </PageContainer>
    );
  }
}
export default connector(UserList);
